import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import {
  AdminAlertText,
  AdminDetailFieldLabel,
  AdminDetailFieldValue,
  AdminFilterDropdownButton,
  AdminFilterMenuItem,
  AdminFormFieldError,
  AdminFormSecondaryAction,
  AdminHeaderAction,
  AdminLinkAction,
  adminFormInputClasses,
  adminFormTextareaClasses,
  AdminMainText,
  AdminMetadataText,
  AdminMetric,
  AdminMetricDelta,
  AdminPageTitle,
  AdminPanelTitle,
  AdminRowActionsButton,
  AdminSearchField,
  AdminStatusBadge,
  AdminSupportingText,
  AdminTabButton,
  AdminTableBody,
  AdminTableHeader,
  AdminTableMeta,
  AdminWidgetTitle,
  ADMIN_FORM_PANEL_CLASS,
  ADMIN_TOOLBAR_CLASS,
} from '../shared/adminUx';

import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';

// LOCKED VALUES - Aligned with Queue & Archive
const CREDENTIAL_CATEGORY_OPTIONS = ['Licence', 'Ticket', 'Badge'];
const VERIFICATION_MODE_OPTIONS = ['Manual', 'Auto', 'Mixed'];
const AUTO_REMOVE_TRIGGER_OPTIONS = ['On Expiry', 'On Document Removal', 'Manual'];
const STATUS_OPTIONS = ['Active', 'Suspended', 'Retired'];
const DOCUMENT_TYPE_OPTIONS = ['Plumbing Licence (VIC/NSW/QLD)', 'ABN', 'Insurance Certificate', 'Police Check Document', 'ID Document', 'WAH Ticket', 'First Aid Certificate', 'Workers Compensation Certificate', 'VicRoads Document', 'Other'];

  const buildDummyRule = (ruleId) => {
  if (ruleId === 'new') return null;
  
  // Return example rule data
  return {
    id: ruleId || 'RULE-LIC-PL-VIC',
    ruleId: ruleId || 'RULE-LIC-PL-VIC',
    name: 'Licensed Plumber (VIC)',
    category: 'Licence',
    criteriaSummary: 'Valid Plumbing Licence + Active ABN',
    status: 'Active',
    icon: 'plumber_badge.svg',
    requiredDocuments: ['Plumbing Licence (VIC)', 'ABN'],
    documentMatchingRules: [
      'Licence holder must match business legal name.',
      'Jurisdiction must match business state.',
      'Licence type must match business category.',
    ],
    expiryBehaviour: [
      'Document must not be expired.',
      'Credential invalid immediately when licence expires.',
    ],
    additionalConditions: [
      'ABN must be active',
      'Business cannot be suspended',
      'Fraud flags must be zero',
    ],
    verificationMode: 'Auto',
    autoRemoveTrigger: 'On Expiry',
    linkedDocuments: [
      { documentType: 'Plumbing Licence (VIC)', fieldMapping: 'Holder Name / Expiry / Jurisdiction', notes: 'Must match trade' },
      { documentType: 'ABN', fieldMapping: 'ABN Number / Status', notes: 'Must be active' },
    ],
    systemBehaviourNotes: [
      'Credential visible in profile and search results.',
      'Hidden if fraud investigation active.',
    ],
    changeLog: [
      { timestamp: '2026-01-01T00:00:00Z', change: 'Rule Created', admin: 'System', notes: '—' },
      { timestamp: '2026-02-12T00:00:00Z', change: 'Criteria Updated', admin: 'Mark', notes: 'Added QLD licences' },
      { timestamp: '2026-02-18T00:00:00Z', change: 'Conditions Updated', admin: 'Sarah', notes: 'ABN must be active' },
    ],
    adminNotes: 'Future improvement: Add SA licence rule once integration ready.',
  };
};

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  return date.toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

const formatDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  return `${formatDate(value)}, ${date.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })}`;
};

const ComplianceRuleEditor = () => {
  const { ruleId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isNew = location.pathname === '/admin/verification/rules/new' || ruleId === 'new';
  const duplicateFrom = location.state?.duplicateFrom;
  const isViewMode = location.state?.viewMode === true;

  const [loading, setLoading] = useState(true);
  const [rule, setRule] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    criteriaSummary: '',
    status: 'Active',
    icon: '',
    requiredDocuments: [],
    documentMatchingRules: [''],
    expiryBehaviour: [''],
    additionalConditions: [''],
    verificationMode: 'Auto',
    autoRemoveTrigger: 'On Expiry',
    linkedDocuments: [{ documentType: '', fieldMapping: '', notes: '' }],
    systemBehaviourNotes: [''],
    adminNotes: '',
  });

  useEffect(() => {
    const fetchRule = async () => {
      // For new rules without duplication, immediately set empty form
      if (isNew && !duplicateFrom) {
        setLoading(false);
        setRule(null);
        setFormData({
          name: '',
          category: '',
          criteriaSummary: '',
          status: 'Active',
          icon: '',
          requiredDocuments: [],
          documentMatchingRules: [''],
          expiryBehaviour: [''],
          additionalConditions: [''],
          verificationMode: 'Auto',
          autoRemoveTrigger: 'On Expiry',
          linkedDocuments: [{ documentType: '', fieldMapping: '', notes: '' }],
          systemBehaviourNotes: [''],
          adminNotes: '',
        });
        return;
      }

      setLoading(true);
      try {
        // TODO: Replace with actual API call
        await new Promise(resolve => setTimeout(resolve, 400));
        
        let ruleData = null;
        if (duplicateFrom) {
          // Duplicate from existing rule
          ruleData = {
            ...duplicateFrom,
            id: 'new',
            ruleId: `RULE-${Date.now()}`,
            name: `${duplicateFrom.name} (Copy)`,
            criteriaSummary: duplicateFrom.criteriaSummary || '',
            verificationMode: duplicateFrom.verificationMode || duplicateFrom.autoGrant || 'Auto',
            autoRemoveTrigger: duplicateFrom.autoRemoveTrigger || duplicateFrom.autoRemove || 'On Expiry',
          };
        } else if (!isNew) {
          ruleData = buildDummyRule(ruleId);
        }

        if (ruleData) {
          // Load existing or duplicated rule data
          setRule(ruleData);
          setFormData({
            name: ruleData.name || '',
            category: ruleData.category || '',
            criteriaSummary: ruleData.criteriaSummary || '',
            status: ruleData.status || 'Active',
            icon: ruleData.icon || '',
            requiredDocuments: ruleData.requiredDocuments || [],
            documentMatchingRules: ruleData.documentMatchingRules && ruleData.documentMatchingRules.length > 0 
              ? ruleData.documentMatchingRules 
              : [''],
            expiryBehaviour: ruleData.expiryBehaviour && ruleData.expiryBehaviour.length > 0
              ? ruleData.expiryBehaviour
              : [''],
            additionalConditions: ruleData.additionalConditions && ruleData.additionalConditions.length > 0
              ? ruleData.additionalConditions
              : [''],
            verificationMode: ruleData.verificationMode || ruleData.autoGrant || 'Auto',
            autoRemoveTrigger: ruleData.autoRemoveTrigger || ruleData.autoRemove || 'On Expiry',
            linkedDocuments: ruleData.linkedDocuments && ruleData.linkedDocuments.length > 0
              ? ruleData.linkedDocuments
              : [{ documentType: '', fieldMapping: '', notes: '' }],
            systemBehaviourNotes: ruleData.systemBehaviourNotes && ruleData.systemBehaviourNotes.length > 0
              ? ruleData.systemBehaviourNotes
              : [''],
            adminNotes: ruleData.adminNotes || '',
          });
        }
        setLoading(false);
      } catch (error) {
        console.error('Error fetching rule:', error);
        setLoading(false);
      }
    };
    
    fetchRule();
  }, [ruleId, isNew, duplicateFrom]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleArrayFieldChange = (field, index, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].map((item, i) => i === index ? value : item),
    }));
  };

  const handleArrayFieldAdd = (field) => {
    setFormData(prev => ({
      ...prev,
      [field]: [...prev[field], ''],
    }));
  };

  const handleArrayFieldRemove = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index),
    }));
  };

  const handleLinkedDocumentChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      linkedDocuments: prev.linkedDocuments.map((doc, i) => 
        i === index ? { ...doc, [field]: value } : doc
      ),
    }));
  };

  const handleLinkedDocumentAdd = () => {
    setFormData(prev => ({
      ...prev,
      linkedDocuments: [...prev.linkedDocuments, { documentType: '', fieldMapping: '', notes: '' }],
    }));
  };

  const handleLinkedDocumentRemove = (index) => {
    setFormData(prev => ({
      ...prev,
      linkedDocuments: prev.linkedDocuments.filter((_, i) => i !== index),
    }));
  };

  const handleRequiredDocumentToggle = (document) => {
    setFormData(prev => ({
      ...prev,
      requiredDocuments: prev.requiredDocuments.includes(document)
        ? prev.requiredDocuments.filter(d => d !== document)
        : [...prev.requiredDocuments, document],
    }));
  };

  const handleSave = () => {
    // Validate required fields
    if (!formData.name || !formData.category || !formData.criteriaSummary) {
      alert('Rule Name, Credential Category, and Criteria Summary are required');
      return;
    }

    // Show warning for edits (forward-only changes)
    if (!isNew && !isViewMode) {
      if (!window.confirm('Changes apply only to future verifications. Historical outcomes will not be affected. Continue saving?')) {
        return;
      }
    }

    // TODO: Implement save API call
    console.log('Saving rule:', formData);
    alert(isNew ? 'Rule created successfully' : 'Rule updated successfully');
    navigate('/admin/verification/rules');
  };

  const handleCancel = () => {
    if (window.confirm('Are you sure you want to cancel? Unsaved changes will be lost.')) {
      navigate('/admin/verification/rules');
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <AdminSupportingText>Loading rule data...</AdminSupportingText>
        </div>
      </AdminLayout>
    );
  }

  // Generate rule ID if new (format: RULE-CATEGORY-IDENTIFIER)
  const displayRuleId = isNew 
    ? `RULE-${formData.category?.substring(0, 3).toUpperCase() || 'NEW'}-${Date.now().toString().slice(-3)}` 
    : (rule?.ruleId || '—');

  return (
    <AdminLayout>
      <div className="tp-container-main">
        <div>
        <div className={ADMIN_TOOLBAR_CLASS}>
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 w-full min-w-0">
            <div className="flex-1">
              <AdminPageTitle className="!mb-0">
                {isViewMode 
                  ? `View Compliance Rule: ${rule?.name || displayRuleId}`
                  : isNew 
                    ? 'Create Compliance Rule' 
                    : `Edit Compliance Rule: ${rule?.name || displayRuleId}`}
              </AdminPageTitle>
              <AdminMainText className="text-mediumgray">
                {isViewMode
                  ? 'Read-only view of the compliance rule configuration.'
                  : isNew 
                    ? 'Define a new compliance rule that controls how credentials are granted, maintained, and revoked. Rule ID is system-generated and immutable.'
                    : 'Update the compliance rule configuration. Rule edits apply forward only and will not affect past decisions or historical outcomes.'}
              </AdminMainText>
            </div>
            {!isViewMode && (
              <div className="flex gap-3">
                <AdminFormSecondaryAction type="button" onClick={handleCancel}
                  >Cancel</AdminFormSecondaryAction>
                <AdminHeaderAction type="button" onClick={handleSave}>
                  Save Rule
                </AdminHeaderAction>
              </div>
            )}
            {isViewMode && (
              <div className="flex gap-3">
                <AdminFormSecondaryAction type="button" onClick={() => navigate('/admin/verification/rules')}>
                  Back to Rules
                </AdminFormSecondaryAction>
                <AdminHeaderAction
                  type="button"
                  onClick={() => navigate(`/admin/verification/rules/${ruleId}`, { state: { viewMode: false } })}
                >
                  Edit Rule
                </AdminHeaderAction>
              </div>
            )}
          </div>
        </div>

        {/* A. Basic Info */}
        <DashboardMainContainer className={ADMIN_FORM_PANEL_CLASS}>
          <div className="space-y-4">
            <AdminPanelTitle className="text-offblack mb-4">Basic Info</AdminPanelTitle>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-1">
                <AdminDetailFieldLabel>
                  Rule Name <span className="text-alertred">*</span>
                </AdminDetailFieldLabel>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  disabled={isViewMode}
                  className={adminFormInputClasses(isViewMode)}
                  placeholder="e.g., Licensed Plumber (VIC)"
                  required
                />
              </div>
              <div className="space-y-1">
                <AdminDetailFieldLabel>
                  Credential Category <span className="text-alertred">*</span>
                </AdminDetailFieldLabel>
                <select
                  value={formData.category}
                  onChange={(e) => handleInputChange('category', e.target.value)}
                  disabled={isViewMode}
                  className={adminFormInputClasses(isViewMode)}
                  required
                >
                  <option value="">Select category</option>
                  {CREDENTIAL_CATEGORY_OPTIONS.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-1 md:col-span-2">
                <AdminDetailFieldLabel>
                  Criteria Summary <span className="text-alertred">*</span>
                </AdminDetailFieldLabel>
                <input
                  type="text"
                  value={formData.criteriaSummary}
                  onChange={(e) => handleInputChange('criteriaSummary', e.target.value)}
                  disabled={isViewMode}
                  className={adminFormInputClasses(isViewMode)}
                  placeholder="e.g., Valid Plumbing Licence + Active ABN"
                  required
                />
                <AdminSupportingText>
                  Plain-English description of the rule
                </AdminSupportingText>
              </div>
              <div className="space-y-1">
                <AdminDetailFieldLabel>
                  Rule ID
                </AdminDetailFieldLabel>
                <input
                  type="text"
                  value={displayRuleId}
                  disabled
                  className={`${adminFormInputClasses(true)} w-full`}
                />
                <AdminSupportingText>
                  System-generated ID (Immutable)
                </AdminSupportingText>
              </div>
              <div className="space-y-1">
                <AdminDetailFieldLabel>
                  Status
                </AdminDetailFieldLabel>
                <select
                  value={formData.status}
                  onChange={(e) => handleInputChange('status', e.target.value)}
                  disabled={isViewMode}
                  className={adminFormInputClasses(isViewMode)}
                >
                  {STATUS_OPTIONS.map(status => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-1 md:col-span-2">
                <AdminDetailFieldLabel>
                  Icon Upload
                </AdminDetailFieldLabel>
                <div className="flex items-center gap-3">
                  <input
                    type="text"
                    value={formData.icon}
                    onChange={(e) => handleInputChange('icon', e.target.value)}
                    disabled={isViewMode}
                    className={`${adminFormInputClasses(isViewMode)} flex-1`}
                    placeholder="e.g., plumber_badge.svg"
                  />
                  {!isViewMode && (
                    <AdminFormSecondaryAction type="button">
                      Upload
                    </AdminFormSecondaryAction>
                  )}
                </div>
                <AdminSupportingText>
                  SVG/PNG format recommended
                </AdminSupportingText>
              </div>
            </div>
          </div>
        </DashboardMainContainer>

        {/* B. Criteria & Requirements */}
        <DashboardMainContainer className={ADMIN_FORM_PANEL_CLASS}>
          <div className="space-y-4">
            <AdminPanelTitle className="text-offblack mb-4">Criteria & Requirements</AdminPanelTitle>
            
            {/* Required Document Types */}
            <div className="space-y-2">
              <AdminDetailFieldLabel>
                1. Required Document Types
              </AdminDetailFieldLabel>
              <div className="flex flex-wrap gap-2">
                {DOCUMENT_TYPE_OPTIONS.map(doc => (
                  <button
                    key={doc}
                    type="button"
                    onClick={() => !isViewMode && handleRequiredDocumentToggle(doc)}
                    disabled={isViewMode}
                    className={`px-3 py-1.5 rounded border text-xs transition-colors ${
                      formData.requiredDocuments.includes(doc)
                        ? 'bg-primaryblue text-white border-primaryblue'
                        : 'bg-white text-offblack border-lightgray hover:border-primaryblue'
                    } ${isViewMode ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                  >
                    {doc}
                  </button>
                ))}
              </div>
            </div>

            {/* Document Matching Rules */}
            <div className="space-y-2">
              <AdminDetailFieldLabel>
                2. Document Matching Rules
              </AdminDetailFieldLabel>
              {formData.documentMatchingRules.map((rule, index) => (
                <div key={index} className="flex gap-2">
                  <input
                    type="text"
                    value={rule}
                    onChange={(e) => handleArrayFieldChange('documentMatchingRules', index, e.target.value)}
                    disabled={isViewMode}
                    className={`${adminFormInputClasses(isViewMode)} flex-1`}
                    placeholder="e.g., Licence holder must match business legal name."
                  />
                  {formData.documentMatchingRules.length > 1 && !isViewMode && (
                    <AdminLinkAction
                      type="button"
                      onClick={() => handleArrayFieldRemove('documentMatchingRules', index)}
                      className="text-alertred"
                    >
                      Remove
                    </AdminLinkAction>
                  )}
                </div>
              ))}
              {!isViewMode && (
                <AdminLinkAction type="button" onClick={() => handleArrayFieldAdd('documentMatchingRules')}>
                  + Add Matching Rule
                </AdminLinkAction>
              )}
            </div>

            {/* Expiry Behaviour */}
            <div className="space-y-2">
              <AdminDetailFieldLabel>
                3. Expiry Behaviour
              </AdminDetailFieldLabel>
              {formData.expiryBehaviour.map((rule, index) => (
                <div key={index} className="flex gap-2">
                  <input
                    type="text"
                    value={rule}
                    onChange={(e) => handleArrayFieldChange('expiryBehaviour', index, e.target.value)}
                    disabled={isViewMode}
                    className={`${adminFormInputClasses(isViewMode)} flex-1`}
                    placeholder="e.g., Document must not be expired."
                  />
                  {formData.expiryBehaviour.length > 1 && !isViewMode && (
                    <AdminLinkAction
                      type="button"
                      onClick={() => handleArrayFieldRemove('expiryBehaviour', index)}
                      className="text-alertred"
                    >
                      Remove
                    </AdminLinkAction>
                  )}
                </div>
              ))}
              {!isViewMode && (
                <AdminLinkAction type="button" onClick={() => handleArrayFieldAdd('expiryBehaviour')}>
                  + Add Expiry Rule
                </AdminLinkAction>
              )}
            </div>

            {/* Additional Conditions */}
            <div className="space-y-2">
              <AdminDetailFieldLabel>
                4. Additional Conditions
              </AdminDetailFieldLabel>
              {formData.additionalConditions.map((condition, index) => (
                <div key={index} className="flex gap-2">
                  <input
                    type="text"
                    value={condition}
                    onChange={(e) => handleArrayFieldChange('additionalConditions', index, e.target.value)}
                    disabled={isViewMode}
                    className={`${adminFormInputClasses(isViewMode)} flex-1`}
                    placeholder="e.g., ABN must be active"
                  />
                  {formData.additionalConditions.length > 1 && !isViewMode && (
                    <AdminLinkAction
                      type="button"
                      onClick={() => handleArrayFieldRemove('additionalConditions', index)}
                      className="text-alertred"
                    >
                      Remove
                    </AdminLinkAction>
                  )}
                </div>
              ))}
              {!isViewMode && (
                <AdminLinkAction type="button" onClick={() => handleArrayFieldAdd('additionalConditions')}>
                  + Add Condition
                </AdminLinkAction>
              )}
            </div>
          </div>
        </DashboardMainContainer>

        {/* C. Auto Logic */}
        <DashboardMainContainer className={ADMIN_FORM_PANEL_CLASS}>
          <div className="space-y-4">
            <AdminPanelTitle className="text-offblack mb-4">Verification & Auto-Remove Logic</AdminPanelTitle>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-1">
                <AdminDetailFieldLabel>
                  Verification Mode
                </AdminDetailFieldLabel>
                <select
                  value={formData.verificationMode}
                  onChange={(e) => handleInputChange('verificationMode', e.target.value)}
                  disabled={isViewMode}
                  className={adminFormInputClasses(isViewMode)}
                >
                  {VERIFICATION_MODE_OPTIONS.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
                <AdminSupportingText>
                  How the rule is verified: Manual, Auto, or Mixed
                </AdminSupportingText>
              </div>
              <div className="space-y-1">
                <AdminDetailFieldLabel>
                  Auto-Remove Trigger
                </AdminDetailFieldLabel>
                <select
                  value={formData.autoRemoveTrigger}
                  onChange={(e) => handleInputChange('autoRemoveTrigger', e.target.value)}
                  disabled={isViewMode}
                  className={adminFormInputClasses(isViewMode)}
                >
                  {AUTO_REMOVE_TRIGGER_OPTIONS.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
                <AdminSupportingText>
                  When the rule stops applying
                </AdminSupportingText>
              </div>
            </div>
          </div>
        </DashboardMainContainer>

        {/* D. Linked Document Map */}
        <DashboardMainContainer className={ADMIN_FORM_PANEL_CLASS}>
          <div className="space-y-4">
            <AdminPanelTitle className="text-offblack mb-4">Linked Document Map</AdminPanelTitle>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-lightgray">
                    <th className="text-left py-3 px-4"><AdminTableHeader>Document Type</AdminTableHeader></th>
                    <th className="text-left py-3 px-4"><AdminTableHeader>Field Mapping</AdminTableHeader></th>
                    <th className="text-left py-3 px-4"><AdminTableHeader>Notes</AdminTableHeader></th>
                    <th className="text-left py-3 px-4"><AdminTableHeader>Action</AdminTableHeader></th>
                  </tr>
                </thead>
                <tbody>
                  {formData.linkedDocuments.map((doc, index) => (
                    <tr key={index} className="border-b border-lightgray">
                      <td className="py-3 px-4">
                        <input
                          type="text"
                          value={doc.documentType}
                          onChange={(e) => handleLinkedDocumentChange(index, 'documentType', e.target.value)}
                          disabled={isViewMode}
                          className={adminFormInputClasses(isViewMode)}
                          placeholder="e.g., Plumbing Licence"
                        />
                      </td>
                      <td className="py-3 px-4">
                        <input
                          type="text"
                          value={doc.fieldMapping}
                          onChange={(e) => handleLinkedDocumentChange(index, 'fieldMapping', e.target.value)}
                          disabled={isViewMode}
                          className={adminFormInputClasses(isViewMode)}
                          placeholder="e.g., Holder Name / Expiry / Jurisdiction"
                        />
                      </td>
                      <td className="py-3 px-4">
                        <input
                          type="text"
                          value={doc.notes}
                          onChange={(e) => handleLinkedDocumentChange(index, 'notes', e.target.value)}
                          disabled={isViewMode}
                          className={adminFormInputClasses(isViewMode)}
                          placeholder="e.g., Must match trade"
                        />
                      </td>
                      <td className="py-3 px-4">
                        {formData.linkedDocuments.length > 1 && !isViewMode && (
                          <AdminLinkAction type="button" onClick={() => handleLinkedDocumentRemove(index)} className="text-alertred">
                            Remove
                          </AdminLinkAction>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {!isViewMode && (
              <AdminLinkAction type="button" onClick={handleLinkedDocumentAdd}>
                + Add Document Mapping
              </AdminLinkAction>
            )}
          </div>
        </DashboardMainContainer>

        {/* E. System Behaviour Notes */}
        <DashboardMainContainer className={ADMIN_FORM_PANEL_CLASS}>
          <div className="space-y-4">
            <AdminPanelTitle className="text-offblack mb-4">System Behaviour Notes</AdminPanelTitle>
            {formData.systemBehaviourNotes.map((note, index) => (
              <div key={index} className="flex gap-2">
                  <input
                    type="text"
                    value={note}
                    onChange={(e) => handleArrayFieldChange('systemBehaviourNotes', index, e.target.value)}
                    disabled={isViewMode}
                    className={`${adminFormInputClasses(isViewMode)} flex-1`}
                    placeholder="e.g., Badge visible in profile and search results."
                  />
                  {formData.systemBehaviourNotes.length > 1 && !isViewMode && (
                    <AdminFormSecondaryAction
                      type="button"
                      className="!text-alertred hover:!border-alertred"
                      onClick={() => handleArrayFieldRemove('systemBehaviourNotes', index)}
                    >
                      Remove
                    </AdminFormSecondaryAction>
                  )}
              </div>
            ))}
            {!isViewMode && (
              <button
                type="button"
                onClick={() => handleArrayFieldAdd('systemBehaviourNotes')}
              >
                + Add Behaviour Note
              </button>
            )}
          </div>
        </DashboardMainContainer>

        {/* F. Rule Change Log */}
        {!isNew && rule?.changeLog && (
          <DashboardMainContainer className={ADMIN_FORM_PANEL_CLASS}>
            <div className="space-y-4">
              <AdminPanelTitle className="text-offblack mb-4">Rule Change Log</AdminPanelTitle>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-lightgray">
                      <th className="text-left py-3 px-4"><AdminTableHeader>Timestamp</AdminTableHeader></th>
                      <th className="text-left py-3 px-4"><AdminTableHeader>Change</AdminTableHeader></th>
                      <th className="text-left py-3 px-4"><AdminTableHeader>Admin</AdminTableHeader></th>
                      <th className="text-left py-3 px-4"><AdminTableHeader>Notes</AdminTableHeader></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rule.changeLog.map((log, index) => (
                      <tr key={index} className="border-b border-lightgray">
                        <td className="py-3 px-4 text-sm text-offblack">{formatDateTime(log.timestamp)}</td>
                        <td className="py-3 px-4 text-sm text-offblack">{log.change}</td>
                        <td className="py-3 px-4 text-sm text-offblack">{log.admin}</td>
                        <td className="py-3 px-4 text-sm text-offblack">{log.notes}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </DashboardMainContainer>
        )}

        {/* G. Admin Notes */}
        <DashboardMainContainer className={ADMIN_FORM_PANEL_CLASS}>
          <div className="space-y-4">
            <AdminPanelTitle className="text-offblack mb-4">Admin Notes</AdminPanelTitle>
            <textarea
              value={formData.adminNotes}
              onChange={(e) => handleInputChange('adminNotes', e.target.value)}
              disabled={isViewMode}
              className={`${adminFormTextareaClasses(isViewMode)} w-full resize-y min-h-[100px]`}
              placeholder="Internal-only free text notes..."
            />
          </div>
        </DashboardMainContainer>

        {!isViewMode && (
          <div className={ADMIN_TOOLBAR_CLASS}>
            <div className="flex justify-end gap-3">
              <AdminFormSecondaryAction type="button" onClick={handleCancel}
                >Cancel</AdminFormSecondaryAction>
              <AdminHeaderAction onClick={handleSave}>
                Save Rule
              </AdminHeaderAction>
            </div>
          </div>
        )}
        {isViewMode && (
          <div className={ADMIN_TOOLBAR_CLASS}>
            <div className="flex justify-end gap-3">
              <AdminFormSecondaryAction type="button" onClick={() => navigate('/admin/verification/rules')}>
                Back to Rules
              </AdminFormSecondaryAction>
              <AdminHeaderAction
                type="button"
                onClick={() => navigate(`/admin/verification/rules/${ruleId}`, { state: { viewMode: false } })}
              >
                Edit Rule
              </AdminHeaderAction>
            </div>
          </div>
        )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default ComplianceRuleEditor;

